import { useMemo, useState, type ReactNode } from "react";

import { Avatar, Box, Divider, Stack, Typography } from "@mui/material";

import {
  ClockIcon,
  LightningIcon,
  BooksIcon,
  ClockCountdownIcon,
  FingerprintSimpleIcon,
} from "@phosphor-icons/react/dist/ssr";

import { SubjectName } from "@/config/subjects";
import { Lesson } from "@/types";

import RecordingIndicator from "./RecordingIndicator";
import ReportDialog from "./ReportDialog";

const BRAND_GREEN = "secondary.main";

function formatLessonTimeRange(
  start: Date,
  durationMinutes: number
): string {
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  const opts: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
  };
  return `${start.toLocaleTimeString(undefined, opts)}–${end.toLocaleTimeString(undefined, opts)}`;
}

function subjectLabel(subjects: { name: SubjectName }[]): string {
  if (subjects.length === 0) return "";
  return subjects.map((s) => s.name).join(", ");
}

function shortLessonId(lesson: Lesson): string {
  const raw = lesson.bookingId?.replace(/[^a-zA-Z0-9]/g, "") || lesson.id;
  return raw.slice(0, 6).toUpperCase();
}

function LessonInfoItem({
  icon,
  label,
}: {
  icon: ReactNode;
  label: string;
}) {
  return (
    <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
      <Divider orientation="vertical" flexItem />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.primary",
          "& svg": {
 width: 16,
height: 16 
},
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="text15L"
      >
        {label}
      </Typography>
    </Stack>
  );
}

interface Props {
  lessonInfo: Lesson;
  participantName: string | undefined;
  participantPhoto: string | undefined;
  participantUserId?: string;
  remainingMinutes: number;
  progressPercent: number;
  showSessionStatus?: boolean;
}

function LessonDetails({
  lessonInfo,
  participantName,
  participantPhoto,
  participantUserId,
  remainingMinutes,
  progressPercent,
  showSessionStatus = true,
}: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);

  const subjectsText = useMemo(
    () => subjectLabel(lessonInfo.subjects),
    [lessonInfo.subjects]
  );

  const timeRange = useMemo(() => {
    const start = lessonInfo.startDateTime.toDate();
    return formatLessonTimeRange(start, lessonInfo.duration);
  }, [lessonInfo.startDateTime, lessonInfo.duration]);

  const displayName = participantName?.trim() || "—";

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flex={1}
        gap={2}
        sx={{ minWidth: 0 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          rowGap={1}
          columnGap={1}
          sx={{
            minWidth: 0,
            flex: 1 
          }}
          >
            <Avatar
              src={participantPhoto}
              alt={displayName}
              sx={{
                width: 32,
                height: 32,
                borderRadius: 999,
                objectFit: "cover",
              }}
            />
            <Typography
              variant="text16B"
            >
              {displayName}
            </Typography>
          {subjectsText ? (
            <>
              <LessonInfoItem
                icon={<BooksIcon size={16} weight="regular" />}
                label={subjectsText}
              />
            </>
          ) : null}
          <LessonInfoItem
            icon={<ClockCountdownIcon size={16} weight="regular" />}
            label={`${lessonInfo.duration} min`}
          />

          <LessonInfoItem icon={<ClockIcon size={16} weight="regular" />} label={timeRange} />
          <LessonInfoItem
            icon={<FingerprintSimpleIcon size={16} weight="regular" />}
            label={`ID: ${shortLessonId(lessonInfo)}`}
          />

          {lessonInfo.type === "instabook" ? (
            <>
              <LessonInfoItem
                icon={<LightningIcon size={16} weight="regular" />}
                label="Instabook"
              />
            </>
          ) : null}
          {showSessionStatus ? (
            <RecordingIndicator isRecording={lessonInfo.recordingEnabled} />
          ) : null}
        </Stack>

        {showSessionStatus ? (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ flexShrink: 0 }}
          >
            <Box
              sx={{
                width: 200,
                height: 8,
                borderRadius: 999,
                border: 1,
                borderColor: BRAND_GREEN,
                bgcolor: "background.default",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${Math.min(100, Math.max(0, progressPercent))}%`,
                  bgcolor: BRAND_GREEN,
                  transition: "width 0.3s linear",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: 15,
                lineHeight: "24px",
                fontWeight: 300,
                color: BRAND_GREEN,
                whiteSpace: "nowrap",
              }}
            >
              {remainingMinutes} min left
            </Typography>
          </Stack>
        ) : null}
      </Stack>

      {isMenuOpen && (
        <Box
          sx={{
 position: "fixed",
inset: 0,
zIndex: 40 
}}
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      <ReportDialog
        open={showReportDialog}
        onClose={() => setShowReportDialog(false)}
        reportedUserId={participantUserId || ""}
      />
    </>
  );
}

export default LessonDetails;
